<template>
  <div class="wrap">
    <div
      v-if="!embedLoading"
      class="embed"
    >
      <LayoutResourceHeader
        :title="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.title ? results.dataset.byId[selectedHash].attributes.title : 'Unknown'"
        type="datasets"
        :embedHeader="true"
        :resourceId="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].id ? results.dataset.byId[selectedHash].id : '-'"
      />

      <div class="embed__chartView">
        <div
          class="embed__controls"
          id="embed__controls"
        >
          <div class="embed__url">
            <router-link target="_blank" :to="'/'">
              <div class="embed__logo" />
            </router-link>

            <p id="copyUrl">
              <span class="embed__url--sm">https://</span>
              <router-link target="_blank" :to="'/'" class="link--white">app.statzon.com</router-link>
              <span>/</span>
              <router-link target="_blank" :to="'/' + results.dataset.byId[selectedHash].type" class="link--white">{{ results.dataset.byId[selectedHash].type }}</router-link>
              <span>/</span>
              <router-link target="_blank" :to="'/' + results.dataset.byId[selectedHash].type + '/' + results.dataset.byId[selectedHash].id" class="link--white">{{ results.dataset.byId[selectedHash].id }}</router-link>
            </p>

            <div
              class="embed__copy"
              :content="copyUrlText"
              v-tippy="{ theme: 'statzon', placement: 'top', arrow: true, animation: 'scale' }"
              @click="copyUrl()"
            >
              <svg :class="!copyTransition ? 'embed__copy--active' : ''" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M13.508 11.504l.93-2.494 2.998 6.268-6.31 2.779.894-2.478s-8.271-4.205-7.924-11.58c2.716 5.939 9.412 7.505 9.412 7.505zm7.492-9.504v-2h-21v21h2v-19h19zm-14.633 2c.441.757.958 1.422 1.521 2h14.112v16h-16v-8.548c-.713-.752-1.4-1.615-2-2.576v13.124h20v-20h-17.633z"/></svg>
              <svg :class="copyTransition ? 'embed__copy--active embed__copy--done' : ''" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M21.856 10.303c.086.554.144 1.118.144 1.697 0 6.075-4.925 11-11 11s-11-4.925-11-11 4.925-11 11-11c2.347 0 4.518.741 6.304 1.993l-1.422 1.457c-1.408-.913-3.082-1.45-4.882-1.45-4.962 0-9 4.038-9 9s4.038 9 9 9c4.894 0 8.879-3.928 8.99-8.795l1.866-1.902zm-.952-8.136l-9.404 9.639-3.843-3.614-3.095 3.098 6.938 6.71 12.5-12.737-3.096-3.096z"/></svg>
            </div>
          </div>

          <LayoutControls
            :controls="controls"
            layout="maximized"
            variant="light"
            id="embed__chartControls"
            @controlsChanged="toggleControls"
          />
        </div>

        <div
          v-show="settings.dataset.display === 'datatable'"
          class="embed__datatable"
        >
          <UtilityDataTable
            :data="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.data_series"
            :scale="results && results.dataset && results.dataset.byId[selectedHash] && results.dataset.byId[selectedHash].attributes.scales[0].attributes.name"
          />
        </div>

        <div
          v-show="results && results.dataset && results.dataset.byId[selectedHash] && settings.dataset.display !== 'datatable'"
          :class="['embed__chart', hasLegend ? '' : 'embed__chart--noLegend']"
        >
          <ChartFull
            :opacity="params['opacity']"
            :width="params['width']"
            :defaultType="params['type']"
            :defaultYear="params['year']"
          />
        </div>

        <div class="embed__footer">
          <div class="embed__source">
            <p>
              <span class="embed__source--sm">Source:&nbsp;</span>
              <a target="_blank" href="https://statzon.com" class="link--white">Statzon</a>
              <span> / </span>
              <router-link target="_blank" :to="'/search?providers=' + (results.dataset.byId[selectedHash].attributes.providers[0].id ? results.dataset.byId[selectedHash].attributes.providers[0].id : '')" class="link--white" v-html="results.dataset.byId[selectedHash].attributes.providers[0].attributes.name ? results.dataset.byId[selectedHash].attributes.providers[0].attributes.name : ''" />
            </p>
          </div>
          <div class="embed__published">
            <p
              class="embed__published--xs"
              :content="publishedDateTooltip"
              v-tippy="{ theme: 'statzon', placement: 'left', arrow: true, animation: 'scale' }"
              v-html="publishedDateMobile"
            />

            <p
              class="embed__published--md"
              :content="publishedDateTooltip"
              v-tippy="{ theme: 'statzon', placement: 'left', arrow: true, animation: 'scale' }"
              v-html="publishedDate"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LayoutResourceHeader from '@/components/layout/ResourceHeader.vue'
import LayoutControls from '@/components/layout/Controls.vue'
import ChartFull from '@/components/chart/Full.vue'
import UtilityDataTable from '@/components/utility/DataTable.vue'
import helperMethods from '@/helpers/utility.js'

export default {
  components: {
    LayoutResourceHeader,
    LayoutControls,
    ChartFull,
    UtilityDataTable
  },
  data () {
    return {
      params: {
        opacity: 0.025,
        width: 400,
        type: null,
        year: null
      },
      copyUrlText: 'Copy URL',
      copyTransition: false,
      controls: {
        'maximized': [{
          'group': [{
            'name': 'dataset.display',
            'value': ['ver_bar', 'hor_bar'],
            'label': ['Display in <u>vertical bar</u> chart', 'Display in <u>horizontal bar</u> chart'],
            'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M5 19h-4v-8h4v8zm6 0h-4v-18h4v18zm6 0h-4v-12h4v12zm6 0h-4v-4h4v4zm1 2h-24v2h24v-2z"/></svg>',
            'iconSize': 'lg',
            'color': 'blue'
          },{
            'name': 'dataset.display',
            'value': ['stacked_ver_bar', 'stacked_hor_bar'],
            'label': ['Display in <u>stacked</u> <u>vertical bar</u> chart', 'Display in <u>stacked</u> <u>horizontal bar</u> chart'],
            'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M4 17.162l-2 .838v-12.972l12-5.028v2.507l-10 4.19v10.465zm18-11.162l-12 5.028v12.972l12-5.028v-12.972zm-14 3.697l10-4.19v-2.507l-12 5.028v12.972l2-.838v-10.465z"/></svg>',
            'iconSize': 'lg',
            'color': 'blue'
          },{
            'name': 'dataset.display',
            'value': ['hor_line', 'ver_line'],
            'label': ['Display in <u>horizontal line</u> chart', 'Display in <u>vertical line</u> chart'],
            'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill-rule="evenodd" clip-rule="evenodd"><path d="M18.799 7.038c-.496-.535-.799-1.252-.799-2.038 0-1.656 1.344-3 3-3s3 1.344 3 3-1.344 3-3 3c-.146 0-.29-.01-.431-.031l-3.333 6.032c.475.53.764 1.231.764 1.999 0 1.656-1.344 3-3 3s-3-1.344-3-3c0-.583.167-1.127.455-1.587l-2.565-3.547c-.281.087-.58.134-.89.134l-.368-.022-3.355 6.069c.451.525.723 1.208.723 1.953 0 1.656-1.344 3-3 3s-3-1.344-3-3 1.344-3 3-3c.186 0 .367.017.543.049l3.298-5.967c-.52-.539-.841-1.273-.841-2.082 0-1.656 1.344-3 3-3s3 1.344 3 3c0 .617-.187 1.191-.507 1.669l2.527 3.495c.307-.106.637-.164.98-.164.164 0 .325.013.482.039l3.317-6.001zm-3.799 7.962c.552 0 1 .448 1 1s-.448 1-1 1-1-.448-1-1 .448-1 1-1zm-6-8c.552 0 1 .448 1 1s-.448 1-1 1-1-.448-1-1 .448-1 1-1z"/></svg>',
            'iconSize': 'lg',
            'color': 'blue'
          },{
            'name': 'dataset.display',
            'value': 'pie',
            'label': 'Display in <u>pie</u> chart',
            'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M13.012 5.007v-1.668l2.802-2.771c.409.136.809.293 1.197.471l-3.999 3.968zm7.089-1.93l-7.089 7.058v.853h.877l7.044-7.076c-.263-.292-.541-.57-.832-.835zm-7.089-1.468l1.437-1.406c-.46-.094-.96-.163-1.437-.203v1.609zm10.789 7.962l-1.386 1.417h1.585c-.04-.47-.106-.964-.199-1.417zm-.363-1.366c-.135-.41-.292-.81-.469-1.199l-3.951 3.982h1.668l2.752-2.783zm-1.063-2.337c-.205-.346-.426-.682-.664-1.004l-6.093 6.124h1.668l5.089-5.12zm-3.225-3.57c-.322-.238-.657-.459-1.003-.665l-5.135 5.104v1.668l6.138-6.107zm-8.15 10.702v-13c-6.161.519-11 5.683-11 11.978 0 6.639 5.382 12.022 12.021 12.022 6.296 0 11.46-4.839 11.979-11h-13z"/></svg>',
            'iconSize': 'md',
            'color': 'blue'
          },{
            'name': 'dataset.display',
            'value': 'datatable',
            'label': 'Display in <u>data table</u>',
            'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M17 13h-10v-1h10v1zm0 2h-10v1h10v-1zm0 3h-10v1h10v-1zm5-16v22h-20v-22h3v1c0 1.103.897 2 2 2s2-.897 2-2v-1h6v1c0 1.103.897 2 2 2s2-.897 2-2v-1h3zm-2 7h-16v13h16v-13zm-12-8c0-.552-.447-1-1-1s-1 .448-1 1v2c0 .552.447 1 1 1s1-.448 1-1v-2zm10 0c0-.552-.447-1-1-1s-1 .448-1 1v2c0 .552.447 1 1 1s1-.448 1-1v-2z"/></svg>',
            'iconSize': 'lg',
            'color': 'blue'
          }]
        }]
      }
    }
  },
  computed: {
    embedLoading () {
      return this.$store.getters['app/getEmbedLoading']
    },
    selectedHash () {
      return this.$store.getters['search/getSelectedHash']
    },
    results () {
      return this.$store.getters['search/getResults']
    },
    settings () {
      return this.$store.getters['app/getSettings']
    },
    hasLegend () {
      if (Object.keys(this.results && this.results.dataset.byId[this.selectedHash] && this.results.dataset.byId[this.selectedHash].attributes.data_series ? this.results.dataset.byId[this.selectedHash].attributes.data_series : {}).length > 1) {
        return true
      }

      return false
    },
    publishedDate () {
      let date = '<span class="embed__published--sm">PUBLISHED:</span> Unknown'

      if (this.results && this.results.dataset.byId[this.selectedHash].attributes.published_at) {
        date = '<span class="embed__published--sm">PUBLISHED:</span> ' + this.results.dataset.byId[this.selectedHash].attributes.published_at
      } else if (this.results && this.results.dataset.byId[this.selectedHash].attributes.created_at) {
        date = '<span class="embed__published--sm">UPLOADED:</span> ' + this.formatDate(this.results.dataset.byId[this.selectedHash].attributes.created_at)
      }

      return date
    },
    publishedDateMobile () {
      //let date = '2024/07/20'
      //return date
      let date = '<span class="embed__published">@&nbsp;</span> Unknown'

      if (this.results && this.results.dataset.byId[this.selectedHash].attributes.published_at) {
        date = '<span class="embed__published">@&nbsp;</span> ' + this.results.dataset.byId[this.selectedHash].attributes.published_at
      } else if (this.results && this.results.dataset.byId[this.selectedHash].attributes.created_at) {
        date = '<span class="embed__published">@&nbsp;</span> ' + this.formatDate(this.results.dataset.byId[this.selectedHash].attributes.created_at)
      }

      return date
    },
    publishedDateTooltip () {
      let tooltip = 'Published date unknown'

      if (this.results && this.results.dataset.byId[this.selectedHash].attributes.published_at) {
        tooltip = 'Published at <b>' + this.results.dataset.byId[this.selectedHash].attributes.published_at + '</b>'
      } else if (this.results && this.results.dataset.byId[this.selectedHash].attributes.created_at) {
        tooltip = 'Uploaded to Statzon at <b>' + this.formatDate(this.results.dataset.byId[this.selectedHash].attributes.created_at) + '</b>'
      }

      return tooltip
    }
  },
  methods: {
    toggleControls (setting, value) {
      if (setting !== 'dataset.download') {
        this.$store.dispatch('app/setSettings', { 'name': setting, 'value': value })
        if (setting === 'dataset.display') {
          this.$router.push({query: {type: value}})
          window.parent.postMessage(
            {
              event: 'statzon_chart_type',
              type: value
            },
            '*'
          );
        }
      }
    },
    copyUrl () {
      this.copyTransition = true
      this.copyUrlText = 'Copied!'
      let textElem = document.getElementById('copyUrl')

      if (document.selection) {
        let range = document.body.createTextRange()
        range.moveToElementText(textElem)
        range.select()
      } else if (window.getSelection) {
        let range = document.createRange()
        range.selectNode(textElem)
        window.getSelection().removeAllRanges()
        window.getSelection().addRange(range)
      }

      document.execCommand('copy')

      let that = this
      setTimeout(function () {
        if (window.getSelection) {
          window.getSelection().removeAllRanges()
        } else if (document.selection) {
          document.selection.empty()
        }

        that.copyUrlText = 'Copy URL'
        that.copyTransition = false
      }, 1000)
    },
    async init () {
      let embedId = this.$route.params.id

      this.$store.dispatch('app/setSettings', { 'name': 'search.view', 'value': 'dataset' })
      if (this.params['type'] && ['ver_bar', 'hor_bar', 'stacked_ver_bar', 'stacked_hor_bar', 'hor_line', 'ver_line', 'pie', 'datatable'].includes(this.params['type'])) {
        this.$store.dispatch('app/setSettings', { 'name': 'dataset.display', 'value': this.params['type']['fullType'] })
      } else {
        this.$store.dispatch('app/setSettings', { 'name': 'dataset.display', 'value': 'ver_bar' })
      }

      await this.$store.dispatch('search/fetchEmbedDataset', embedId)
      this.$store.dispatch('app/setEmbedLoading', false)
    },
    ...helperMethods
  },
  mounted () {
    if (this.$route.query.opacity) {
      this.params['opacity'] = this.$route.query.opacity / 100
    }
    if (this.$route.query.width) {
      this.params['width'] = this.$route.query.width
    }
    if (this.$route.query.type) {
      let fullType = this.$route.query.type
      let type = ''
      let stack = false
      let axis = 'yAxis'

      if (fullType.includes('_')) {
        if (fullType.includes('stacked')) {
          stack = 'total'
          fullType = fullType.replace('stacked_', '')
        }
        if (fullType.includes('ver_bar')) {
          type = 'bar'
        } else if (fullType.includes('hor_bar')) {
          axis = 'xAxis'
          type = 'bar'
        } else if (fullType.includes('hor_line')) {
          type = 'line'
        } else if (fullType.includes('ver_line')) {
          axis = 'xAxis'
          type = 'line'
        }
      } else {
        type = fullType
        stack = false,
        axis = 'xAxis'
      }

      this.params['type'] = {
        type: type,
        fullType: fullType,
        stack: stack,
        axis: axis
      }

      if (fullType === 'pie') {
        if (this.$route.query.year) {
          this.params['year'] = this.$route.query.year
        }
      }
    }

    this.init()
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

.wrap {
  width: calc(100% - 2px);
  height: 100%;
  border-width: 2px 1px;
  border-style: solid;
  border-color: $black;
}

.embed {
  @include font('content--md');

  @media screen and (min-width: $breakpointLg) {
    @include font('content--lg');
  }

  &__logo {
    //background-image: url('/img/statzon_z_white_animated.svg');
    background-image: url('/img/statzon_z_white.png');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: 0px 0px;
    width: 26px;
    height: 26px;
    margin-right: 0.5rem;

    @media screen and (min-width: $breakpointLg) {
      margin-right: 0.75rem;
    }
  }

  &__url {
    position: relative;
    display: flex;
    align-items: center;
    margin: 0 0.5rem;
    font-weight: 500;

    &--sm {
      display: none;

      @media screen and (min-width: $breakpointSm) {
        display: inline-flex;
      }
    }

    p {
      display: none;

      @media screen and (min-width: $breakpointXs) {
        overflow: hidden;
        display: -webkit-box;
        text-overflow: ellipsis;
        -webkit-line-clamp: 1;
        line-clamp: 1;
        -webkit-box-orient: vertical;
      }
    }

    a {
      color: rgba($white, 0.8);
    }

    /deep/ span {
      color: rgba($white, 0.4);
    }
  }

  &__copy {
    display: none;

    @media screen and (min-width: $breakpointXs) {
      position: relative;
      display: flex;
      fill: rgba($white, 0.3);
      min-width: 18px;
      width: 18px;
      height: 18px;
      margin: 0 0.5rem;
      transition: fill 0.3s ease-out, transform 0.15s ease-out;
      cursor: pointer;
    }

    @media screen and (min-width: $breakpointLg) {
      min-width: 20px;
      width: 20px;
      height: 20px;
      margin: 0 0.75rem;
    }

    &:hover {
      fill: rgba($white, 0.9);
      transform: scale(1.2);
    }

    svg {
      position: absolute;
      opacity: 0;
      transition: opacity 0.3s ease-out;
    }

    &--active {
      opacity: 1 !important;
    }

    &--done {
      fill: $blue;
    }
  }

  &__controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: $bg-4;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;
    height: 36px;

    @media screen and (min-width: $breakpointLg) {
      height: 40px;
    }
  }

  &__chart {
    background-color: $bg-2;
    border-bottom: 1px solid $black;
    height: 100%;
  }

  &__datatable {
    border-bottom: 1px solid $black;
  }

  &__footer {
    background: $bg-2;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;
    padding: 0px 8px;
    height: 36px;

    @media screen and (min-width: $breakpointLg) {
      height: 40px;
    }

    p {
      margin: 0rem;
      font-weight: 500;
      overflow: hidden;
      display: -webkit-box;
      text-overflow: ellipsis;
      -webkit-line-clamp: 1;
      line-clamp: 1;
      -webkit-box-orient: vertical;
    }

    /deep/ span {
      color: rgba($white, 0.5);
      text-transform: uppercase;
    }
  }

  &__source {
    margin-right: 1rem;

    /deep/ &--sm {
      display: none;

      @media screen and (min-width: $breakpointSm) {
        display: inline-flex;
      }
    }
  }

  &__published {
    text-align: right;

    /deep/ &--sm {
      display: none;

      @media screen and (min-width: $breakpointSm) {
        display: inline-flex;
      }
    }
  }
}

/deep/ .embed__published--xs {
  display: inline-flex !important;

  @media screen and (min-width: $breakpointSm) {
    display: none !important;
  }
}

/deep/ .embed__published--md {
  display: none !important;

  @media screen and (min-width: $breakpointSm) {
    display: block !important;
  }
}

/deep/ .chart__render {
  height: calc(100vh - 224px) !important;
}

/deep/ .embed__datatable .table {
  height: calc(100vh - 185px) !important;
}

.embed__chart--noLegend /deep/ .chart__render {
  height: calc(100vh - 186px) !important;
}

@media screen and (min-width: $breakpointSm) {
  /deep/ .chart__render {
    height: calc(100vh - 190px) !important;
  }

  /deep/ .embed__datatable .table {
    height: calc(100vh - 151px) !important;
  }

  .embed__chart--noLegend /deep/ .chart__render {
    height: calc(100vh - 152px) !important;
  }
}

@media screen and (min-width: $breakpointMd) {
  /deep/ .chart__render, /deep/ .legend--desktop {
    height: calc(100vh - 152px) !important;
  }

  /deep/ .embed__datatable .table {
    height: calc(100vh - 151px) !important;
  }

  /deep/ .scroller {
    max-height: calc(100vh - 150px) !important;
  }

  .embed__chart--noLegend /deep/ .chart__render {
    height: calc(100vh - 152px) !important;
  }
}

@media screen and (min-width: $breakpointLg) {
  /deep/ .chart__render, /deep/ .legend--desktop {
    height: calc(100vh - 184px) !important;
  }

  /deep/ .embed__datatable .table {
    height: calc(100vh - 183px) !important;
  }

  /deep/ .scroller {
    max-height: calc(100vh - 182px) !important;
  }

  .embed__chart--noLegend /deep/ .chart__render {
    height: calc(100vh - 184px) !important;
  }
}

</style>
